import React, { MouseEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@novo-electronique/react-circular-progress';
import classNames from 'classnames';

import { FileUploadedState, IFileUploaded } from './file-uploaded';
import { getFileIcon } from './utils';

export enum FileUploadedAction {
  Download = 'download',
  Remove = 'remove',
}

export interface IFileUploadedProps {
  file: IFileUploaded;
  onClick?: (file: IFileUploaded) => void;
  disabled?: boolean;
  labelsNamespace?: string;
  withStatus?: boolean;
  actions?: Partial<{ [action in FileUploadedAction]: (file: IFileUploaded) => void | Promise<void> }>;
}

function FileUploaded({
  file,
  onClick,
  disabled,
  labelsNamespace,
  withStatus,
  actions = {},
}: IFileUploadedProps) {
  const { t } = useTranslation();
  const isMounted = useRef(false);
  const [actionsProcessing, setActionsProcessing] = useState<
    Partial<{ [action in FileUploadedAction]: boolean }>
  >({});

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  });

  const eventHandler = async (e: MouseEvent, action: FileUploadedAction) => {
    e.stopPropagation();
    setActionsProcessing({ ...actionsProcessing, [action]: true });

    const callback = actions[action];
    try {
      await callback(file);
    } finally {
      if (isMounted.current) {
        setActionsProcessing({ ...actionsProcessing, [action]: false });
      }
    }
  };

  const isLoading = !file.id && !file.state;
  const isCompleted = file.id || file.state === FileUploadedState.Completed;
  const isButton = onClick && !disabled && isCompleted;

  const actionsDisabled: { [action in FileUploadedAction]: boolean } = {
    [FileUploadedAction.Remove]: isLoading || disabled,
    [FileUploadedAction.Download]:
      !isCompleted || disabled || Boolean(actionsProcessing[FileUploadedAction.Download]),
  };

  const fileClassName = classNames('novoFileUploaded', {
    'novoFileUploaded--is-loading': isLoading,
    'novoFileUploaded--faulty': file.state === FileUploadedState.Faulty,
  });
  const statusClassName = classNames('novoFileUploaded__status', {
    'novoFileUploaded__status--loading': isLoading,
    'novoFileUploaded__status--completed': isCompleted,
    'novoFileUploaded__status--fault': file.state === FileUploadedState.Faulty,
  });

  return (
    <article
      className={fileClassName}
      role={(isButton && 'button') || undefined}
      onClick={isButton ? () => onClick(file) : undefined}
    >
      <div className="novoFileUploaded__info">
        <span className="novoFileUploaded__info__icon material-icons">{getFileIcon(file.type)}</span>
        <h3 className="novoFileUploaded__info__name">{file.name}</h3>
      </div>

      {withStatus && <div className={statusClassName}>{isLoading && <CircularProgress />}</div>}

      <div className="novoFileUploaded__actions">
        {Object.keys(actions)
          .filter((action) => actions[action])
          .map((action) => (
            <button
              key={action}
              title={(labelsNamespace && t(`${labelsNamespace}.${action}`)) || undefined}
              className={`novoFileUploaded__actions__button novoFileUploaded__actions__button--${action} material-icons`}
              type="button"
              disabled={actionsDisabled[action]}
              onClick={(e) => eventHandler(e, action as FileUploadedAction)}
            />
          ))}
      </div>
    </article>
  );
}

export default FileUploaded;
